import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Button, Tooltip, Card } from 'antd';
import {
  UploadOutlined,
  ReadOutlined,
  FileWordOutlined,
  FilePdfOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { demoBooks, IBookDemo } from './demoData';
import DemoHeader from './DemoHeader';
import { useNavigate } from 'react-router-dom';

// Styles adapted from ManageBooks.tsx but simplified for the demo

const Container = styled.div`
  background: var(--bg-app);
  padding: 2em;
  min-height: calc(100vh - 64px);

  @media (max-width: 768px) {
    padding: 1em;
  }
`;

const StyledCard = styled(Card)`
  background: var(--surface-base);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: none;
  padding: 2em;

  @media (max-width: 768px) {
    padding: 1em;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderText = styled.h2`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--text-main);
  display: flex;
  align-items: center;
`;

const AuthorBooksContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  background: var(--surface-base);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: none;
  overflow: hidden;

  content-visibility: auto;
  contain-intrinsic-size: 600px;
`;

const AuthorHeader = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--text-main);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  background: var(--surface-elevated);
  border-left: 4px solid var(--text-info);
`;

const BookCountBadge = styled.span`
  font-size: 0.75rem;
  background: var(--surface-elevated);
  color: var(--text-secondary);
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 8px;
  font-weight: normal;
`;

const BookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  grid-gap: 24px;
  margin-top: 16px;
  padding: 0 20px 20px 20px;

  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    grid-gap: 16px;
  }
`;

const BookItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const BookCover = styled.div`
  position: relative;
  border-radius: 8px;
  height: 220px;
  width: 140px;
  margin-bottom: 12px;
  border: 1px solid var(--border-subtle);
  box-shadow: none;
  overflow: hidden;
  background: var(--surface-elevated);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.03);
    border-color: var(--border-info);
  }
`;

const BookCoverImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BookTitle = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-elevated);
  color: var(--text-main);
  text-align: center;
  padding: 10px;
  font-weight: 500;
`;

const BookTitleOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 12px 8px;
  font-size: 0.85rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;

  ${BookCover}:hover & {
    opacity: 1;
  }
`;

const BookText = styled.div`
  font-size: 0.9rem;
  margin-top: 5px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 140px;
  color: var(--text-main);
  font-weight: 500;
`;

const FileTypeBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: var(--surface-base);
  border: 1px solid var(--border-subtle);
  border-radius: 4px;
  padding: 4px;
  box-shadow: none;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const getFileTypeIcon = (uploadType?: string) => {
  if (!uploadType) return null;
  switch ((uploadType || '').toLowerCase()) {
    case 'word':
      return <FileWordOutlined style={{ fontSize: 16, color: '#2b5797' }} />;
    case 'epub':
      return <ReadOutlined style={{ fontSize: 16, color: '#6ba54a' }} />;
    case 'pdf':
      return <FilePdfOutlined style={{ fontSize: 16, color: '#e24329' }} />;
    default:
      return <BookOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />;
  }
};

const DemoManageBooks: React.FC = () => {
  const books = useMemo<IBookDemo[]>(() => demoBooks, []);
  const navigate = useNavigate();

  const shortTitle = useCallback((title: string): string => {
    return title.length > 20 ? `${title.substring(0, 20)}...` : title;
  }, []);

  // Group demo books by author (no series sub-cards in demo)
  const booksByAuthor = useMemo(() => {
    const map = new Map<string, IBookDemo[]>();
    books.forEach((b) => {
      const author = b.author || 'Unknown';
      if (!map.has(author)) map.set(author, []);
      map.get(author)!.push(b);
    });
    // sort authors A-Z and titles A-Z within each author
    const entries = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    entries.forEach(([, arr]) => {
      arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    });
    return entries;
  }, [books]);

  return (
    <>
      <DemoHeader />
      <Container>
        <StyledCard>
          <HeaderContainer>
            <HeaderText>My Books Library</HeaderText>
            <Tooltip title="Disabled in demo">
              <Button type="primary" icon={<UploadOutlined />} size="large" disabled>
                Upload Book
              </Button>
            </Tooltip>
          </HeaderContainer>

          {booksByAuthor.map(([author, authorBooks]) => (
            <AuthorBooksContainer key={author}>
              <AuthorHeader>
                {author}
                <BookCountBadge>{authorBooks.length} books</BookCountBadge>
              </AuthorHeader>

              <BookGrid>
                {authorBooks.map((book) => (
                  <BookItem key={book.bookguid}>
                    
                      <BookCover onClick={() => navigate(`/demo/books/${book.bookguid}`)}>
                        {book.coverUrl ? (
                          <BookCoverImg
                            loading="lazy"
                            decoding="async"
                            src={book.coverUrl}
                            alt={`${book.title} cover`}
                            width={140}
                            height={220}
                          />
                        ) : (
                          <BookTitle>{book.title}</BookTitle>
                        )}

                        {book.coverUrl && (
                          <BookTitleOverlay>{book.title}</BookTitleOverlay>
                        )}

                        {book.uploadType && (
                          <Tooltip title={book.uploadType === 'Word' ? 'Word' : book.uploadType} destroyOnHidden mouseEnterDelay={0.2}>
                            <FileTypeBadge>{getFileTypeIcon(book.uploadType)}</FileTypeBadge>
                          </Tooltip>
                        )}
                      </BookCover>
                    
                    <BookText title={book.title}>{shortTitle(book.title)}</BookText>
                  </BookItem>
                ))}
              </BookGrid>
            </AuthorBooksContainer>
          ))}
        </StyledCard>
      </Container>
    </>
  );
};

export default DemoManageBooks;
